import type { UseFormReturn } from 'react-hook-form';
import { Wifi } from 'lucide-react';
import { useHosts } from '../../../../hooks/useHosts';
import { useSipInterfaces } from '../../../../hooks/useSipInterfaces';
import type { SipPeerFormData } from '../schema';

/** Identity tab: SIP identity override, runtime metadata, and selected interface summary. */
export function IdentityTab({ form, profileLocked = false, profileName }: { form: UseFormReturn<SipPeerFormData>; profileLocked?: boolean; profileName?: string | null }) {
  const { data: hostsResponse, isLoading: hostsLoading } = useHosts();
  const hosts = hostsResponse?.data || [];
  const { data: sipInterfacesData } = useSipInterfaces();
  const peerType = form.watch('type');

  const selectedInterfaceId = form.watch('sipInterfaceId');
  const selectedInterface = sipInterfacesData?.find(i => i.id === selectedInterfaceId);

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">Identity</h3>
        <p className="text-sm text-gray-600 mb-4">
          SIP identity override and runtime metadata consumed by the SIP engine.
        </p>
      </div>

      {/* SIP identity override (optional) - advanced identity override */}
      <div className="bg-gray-50 p-4 rounded-lg">
        <h4 className="font-medium text-gray-900 mb-2">SIP Identity Override</h4>
        <p className="text-xs text-gray-500 mb-3">
          Override the SIP identity used for Via/Contact and TLS. If not set, the SIP interface identity or default identity is used.
        </p>
        <select
          {...form.register('hostId')}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
          disabled={hostsLoading}
        >
          <option value="">
            {hostsLoading ? 'Loading SIP identities...' : 'Use interface/default identity'}
          </option>
          {hosts.map((host) => (
            <option key={host.id} value={host.id}>
              {host.name} {host.domain ? `(${host.domain})` : ''}{host.isDefault ? ' - Default' : ''}
            </option>
          ))}
        </select>
      </div>

      {/* Runtime Metadata */}
      <div className="bg-gray-50 p-4 rounded-lg space-y-4">
        <h4 className="font-medium text-gray-900">Runtime Metadata</h4>
        {profileLocked && (
          <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
            Session timer fields are managed by attached profile {profileName ? <span className="font-medium">{profileName}</span> : null}.
          </div>
        )}
        <p className="text-xs text-gray-500">
          Explicit metadata consumed by the SIP engine for peer-specific SIP headers and session timers.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">User-Agent override</label>
            <input
              {...form.register('metadata.userAgent')}
              type="text"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
              placeholder="Mediant VE SBC/v.7.40A.500.009"
            />
            <p className="text-xs text-gray-500 mt-1">Teams AudioCodes emulation uses the Mediant VE User-Agent.</p>
          </div>
          {peerType === 'teams' && (
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">Teams SBC FQDN</label>
              <input
                {...form.register('metadata.teamsConfig.sbcFqdn')}
                type="text"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                placeholder="sip-prod-01.example.com"
              />
              <p className="text-xs text-gray-500 mt-1">
                Public SBC FQDN presented to Microsoft Teams Direct Routing.
              </p>
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Outbound Min-SE seconds</label>
            <input
              {...form.register('metadata.outboundMinSessionExpiresSeconds', { valueAsNumber: true })}
              type="number"
              min="90"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="900"
              disabled={profileLocked}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Inbound Min-SE seconds</label>
            <input
              {...form.register('metadata.minSessionExpiresSeconds', { valueAsNumber: true })}
              type="number"
              min="90"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="90"
              disabled={profileLocked}
            />
          </div>
        </div>
      </div>

      {/* SIP Interface Summary - shows the interface selected on the Interface tab */}
      <div className="bg-gray-50 p-4 rounded-lg space-y-4">
        <h4 className="font-medium text-gray-900 flex items-center">
          <Wifi className="w-5 h-5 mr-2 text-blue-600" />
          SIP Interface <span className="ml-2 text-red-500 text-sm">*</span>
        </h4>
        <p className="text-xs text-gray-500 mb-2">
          The SIP interface selected on the Interface tab. Required for all peers.
        </p>

        {selectedInterface ? (
          <div className="flex items-center p-3 border border-blue-500 bg-blue-50 rounded-lg">
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <span className="font-medium text-gray-900">{selectedInterface.interfaceId}</span>
                <span className={`px-2 py-0.5 text-xs rounded-full ${
                  selectedInterface.protocol === 'TLS'
                    ? 'bg-green-100 text-green-800'
                    : selectedInterface.protocol === 'TCP'
                    ? 'bg-blue-100 text-blue-800'
                    : 'bg-gray-100 text-gray-800'
                }`}>
                  {selectedInterface.protocol}
                </span>
              </div>
              <p className="text-xs text-gray-500">
                {selectedInterface.bindAddress}:{selectedInterface.port}
                {selectedInterface.description && ` - ${selectedInterface.description}`}
              </p>
            </div>
          </div>
        ) : (
          <div className="text-center py-4 border border-dashed border-gray-300 rounded-lg">
            <p className="text-sm text-gray-500">
              No interface selected. Choose one on the Interface tab.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
